/**
 * Checkout diagnostic event contract (schema v1).
 *
 * ONE definition for the events that explain WHY a visitor did not get from cart to a
 * submitted payment, shared by every client surface (cart, information step, payment
 * step, loyalty credit). The funnel events (`checkout_started`, `order_completed`, …)
 * say THAT someone dropped; these say where the submit was refused, which guard fired
 * and what the loyalty credit request answered:
 *
 *  - `payment_submit_blocked`      a payment submit was refused (disabled tap, guard,
 *                                  validation, failed mutation) with a stable reason.
 *  - `checkout_information_viewed` the information step rendered.
 *  - `checkout_information_abandoned`
 *                                  the information step was left without a successful
 *                                  submit (pagehide, tab hidden or in-app unmount), once
 *                                  per mount, with allowlisted field keys of its progress.
 *  - `checkout_information_first_interaction`
 *                                  the first focus on, or first input or change in, a
 *                                  field of the information step, once per mount: which
 *                                  field and after how long.
 *  - `cart_checkout_clicked`       the cart's checkout CTA was pressed (or refused).
 *  - `loyalty_credit_result`       one loyalty credit request settled (ok/error/…).
 *  - `checkout_total_unlocked`     the payable total became confirmed and submittable.
 *  - `checkout_sign_in_failed`     an in-checkout sign-in attempt failed.
 *  - `checkout_sign_in_succeeded`  an in-checkout sign-in went through (its denominator).
 *  - `checkout_information_submit_failed`
 *                                  the information submit stopped at a Saleor mutation:
 *                                  no answer (`error_kind: "transport"`) or a refusal
 *                                  (`"mutation"`, allowlisted codes and field keys only).
 *  - `discount_code_failed`        applying or removing a discount code failed; stage,
 *                                  kind and Saleor's code, never the code itself.
 *  - `payment_status_poll_failed`  a status poll of the pending screen failed (HTTP
 *                                  status, network or unreadable body); throttled, no
 *                                  raw transaction id.
 *  - `cart_opened`                 the cart drawer opened (once per opening): trigger,
 *                                  line/item counts, total band once settled.
 *  - `cart_line_changed`           a quantity change or removal in the drawer or on the
 *                                  cart page settled: action, outcome; throttled.
 *
 * Client-safe and pure: no server imports, no PostHog call. Event names and the reason
 * vocabulary live here so no surface hand-rolls a string literal.
 *
 * Versioning: every diagnostic event carries `event_schema_version` (the same property
 * name as `add_to_cart`, see add-to-cart-events.ts) with
 * `CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION` as its value. Never `schema_version` or a
 * per-event variant: one property name keeps a single PostHog column for all of them.
 *
 * Money: every amount (and the `totalBucket` input) is the CHANNEL currency of the
 * Saleor total (EUR/USD settlement) — NEVER the visitor's display/reference currency.
 * Buckets are only comparable because the input basis is fixed.
 */

import type { CheckoutErrorCode } from "@/checkout/graphql";
import { CHECKOUT_LOG_FIELD_KEYS } from "@/lib/observability/checkout-client-error-contract";

/**
 * Bump only for an INCOMPATIBLE change to the property shapes — a renamed, removed or
 * re-typed property. Additive vocabulary (a new reason, surface or trigger) does NOT
 * bump: existing filters keep matching and simply see one more value.
 */
export const CHECKOUT_DIAGNOSTIC_SCHEMA_VERSION = 1;

export const PAYMENT_SUBMIT_BLOCKED_EVENT = "payment_submit_blocked";
export const CHECKOUT_INFORMATION_VIEWED_EVENT = "checkout_information_viewed";
export const CART_CHECKOUT_CLICKED_EVENT = "cart_checkout_clicked";
export const LOYALTY_CREDIT_RESULT_EVENT = "loyalty_credit_result";
export const CHECKOUT_TOTAL_UNLOCKED_EVENT = "checkout_total_unlocked";
export const CHECKOUT_SIGN_IN_FAILED_EVENT = "checkout_sign_in_failed";
export const CHECKOUT_SIGN_IN_SUCCEEDED_EVENT = "checkout_sign_in_succeeded";
export const CHECKOUT_INFORMATION_SUBMIT_FAILED_EVENT = "checkout_information_submit_failed";
export const DISCOUNT_CODE_FAILED_EVENT = "discount_code_failed";
export const PAYMENT_STATUS_POLL_FAILED_EVENT = "payment_status_poll_failed";
export const CHECKOUT_INFORMATION_ABANDONED_EVENT = "checkout_information_abandoned";
export const CHECKOUT_INFORMATION_FIRST_INTERACTION_EVENT = "checkout_information_first_interaction";

/**
 * What ended a visit of the information step without a submit; the first one wins:
 * `pagehide` (tab closed, reloaded or navigated away, also into the back-forward cache),
 * `visibility_hidden` (the page left view: tab or app sent to the background, where mobile
 * Safari often kills a tab without `pagehide`, but in some browsers also a tab close,
 * whose `visibilitychange` comes before `pagehide`) or `unmount` (left inside the app,
 * e.g. back to a product page, which fires no `pagehide`; with cacheComponents that is an
 * Activity hide). The trigger therefore does not separate a pause from a final exit.
 */
export const CHECKOUT_INFORMATION_ABANDON_TRIGGERS = ["pagehide", "visibility_hidden", "unmount"] as const;
export type CheckoutInformationAbandonTrigger = (typeof CHECKOUT_INFORMATION_ABANDON_TRIGGERS)[number];

/**
 * The only field keys the information-step progress events may carry. Key lists follow
 * this order, which is roughly the form's (the DOM puts `streetAddress1` before
 * `companyName`, and the saved-address selector before `brTaxId`).
 * Equal to the DOM ids of the controls where one exists (`email`, `country`, the address
 * inputs, `brTaxId`, `legalConfirmation`); `address` is the saved-address selector and
 * `shippingMethod` the delivery options. A key outside this list is dropped, never sent as
 * free text and never folded into `other`.
 */
export const CHECKOUT_INFORMATION_PROGRESS_FIELD_KEYS = [
	"email",
	"createAccount",
	"password",
	"country",
	"firstName",
	"lastName",
	"companyName",
	"streetAddress1",
	"streetAddress2",
	"postalCode",
	"city",
	"cityArea",
	"countryArea",
	"phone",
	"brTaxId",
	"address",
	"shippingMethod",
	"legalConfirmation",
] as const;
export type CheckoutInformationProgressFieldKey = (typeof CHECKOUT_INFORMATION_PROGRESS_FIELD_KEYS)[number];

/**
 * What started a submit of the information step (`submit_trigger` on
 * `checkout_information_validation_failed`): the keyboard's Enter/Weiter key in a field,
 * the mobile sticky bar's button, the in-flow button, or a submitter the step cannot name.
 */
export const CHECKOUT_INFORMATION_SUBMIT_TRIGGERS = [
	"enter_key",
	"sticky_bar",
	"inline_button",
	"unknown",
] as const;
export type CheckoutInformationSubmitTrigger = (typeof CHECKOUT_INFORMATION_SUBMIT_TRIGGERS)[number];

/** Why the e-mail failed the client check: left empty, or not an address Saleor would take. */
export const CHECKOUT_INFORMATION_EMAIL_ERROR_KINDS = ["required", "invalid"] as const;
export type CheckoutInformationEmailErrorKind = (typeof CHECKOUT_INFORMATION_EMAIL_ERROR_KINDS)[number];

/**
 * `error_kind` of `payment_status_poll_failed` (the same property name as on the other
 * failure events, its own values): a non-2xx answer (`http`, with `http_status`), no
 * answer at all (`network`), or a 2xx answer that could not be read (`invalid_body`).
 */
export type PaymentStatusPollErrorKind = "http" | "network" | "invalid_body";

export const CART_OPENED_EVENT = "cart_opened";

/**
 * What opened the cart drawer: an add-to-cart (the runner opens it to show the new
 * line), the header cart button, or anything else. `other` is also the fallback for an
 * opener that names no trigger.
 */
export const CART_OPEN_TRIGGERS = ["add_to_cart", "header", "other"] as const;
export type CartOpenTrigger = (typeof CART_OPEN_TRIGGERS)[number];

export const CART_LINE_CHANGED_EVENT = "cart_line_changed";

/**
 * What the buyer asked for, relative to the quantity the cart showed: higher, lower, the
 * same or an unknown shown quantity (`set`, an absolute target), or remove.
 */
export const CART_LINE_CHANGE_ACTIONS = ["increment", "decrement", "set", "remove"] as const;
export type CartLineChangeAction = (typeof CART_LINE_CHANGE_ACTIONS)[number];

/** Where the change was made: the cart drawer or the cart page. */
export const CART_LINE_CHANGE_SURFACES = ["drawer", "cart_page"] as const;
export type CartLineChangeSurface = (typeof CART_LINE_CHANGE_SURFACES)[number];

/**
 * What the browser can say about a failure beyond the server's answer: the server action
 * call itself threw (network, a deploy that retired the action). The Saleor code of a
 * refusal is in the server's `[cart] line change` log line, not in the browser.
 */
export const CART_LINE_CHANGE_ERROR_CODES = ["action_threw"] as const;
export type CartLineChangeErrorCode = (typeof CART_LINE_CHANGE_ERROR_CODES)[number];

/**
 * How a checkout mutation failed, shared by the failure events: Saleor answered with
 * field/domain errors (`mutation`), no usable answer arrived (`transport`), or the call
 * threw (`exception`).
 */
export type CheckoutMutationErrorKind = "mutation" | "transport" | "exception";

/**
 * Saleor `CheckoutErrorCode` values an event may carry. The checkout mutations and the
 * promo-code mutations share this enum. Anything else is reported as `other`, so a code
 * a newer Saleor introduces shows up as a count, never as free text.
 */
export const CHECKOUT_SALEOR_ERROR_CODES = [
	"BILLING_ADDRESS_NOT_SET",
	"CHANNEL_INACTIVE",
	"CHECKOUT_NOT_FULLY_PAID",
	"DELIVERY_METHOD_NOT_APPLICABLE",
	"EMAIL_NOT_SET",
	"GIFT_CARD_NOT_APPLICABLE",
	"GRAPHQL_ERROR",
	"INACTIVE_PAYMENT",
	"INSUFFICIENT_STOCK",
	"INVALID",
	"INVALID_SHIPPING_METHOD",
	"MISSING_ADDRESS_DATA",
	"MISSING_CHANNEL_SLUG",
	"NON_EDITABLE_GIFT_LINE",
	"NON_REMOVABLE_GIFT_LINE",
	"NOT_FOUND",
	"NO_LINES",
	"PAYMENT_ERROR",
	"PRICE_OVERRIDE_REASON_WITHOUT_OVERRIDE",
	"PRODUCT_NOT_PUBLISHED",
	"PRODUCT_UNAVAILABLE_FOR_PURCHASE",
	"QUANTITY_GREATER_THAN_LIMIT",
	"REQUIRED",
	"SHIPPING_ADDRESS_NOT_SET",
	"SHIPPING_CHANGE_FORBIDDEN",
	"SHIPPING_METHOD_NOT_APPLICABLE",
	"SHIPPING_METHOD_NOT_SET",
	"SHIPPING_NOT_REQUIRED",
	"TAX_ERROR",
	"UNAVAILABLE_VARIANT_IN_CHANNEL",
	"UNIQUE",
	"VOUCHER_NOT_APPLICABLE",
	"ZERO_QUANTITY",
] as const satisfies readonly CheckoutErrorCode[];
type CheckoutSaleorErrorCode = (typeof CHECKOUT_SALEOR_ERROR_CODES)[number];

const MAX_REPORTED_SALEOR_ERRORS = 10;

/** An allowlisted Saleor code, `other` for any other string, `null` when there is none. */
export function saleorErrorCode(code: unknown): CheckoutSaleorErrorCode | "other" | null {
	if (typeof code !== "string") return null;
	return (CHECKOUT_SALEOR_ERROR_CODES as readonly string[]).includes(code)
		? (code as CheckoutSaleorErrorCode)
		: "other";
}

/**
 * Saleor field/domain errors reduced to what an event may carry: the allowlisted codes
 * and the field KEYS from `CHECKOUT_LOG_FIELD_KEYS` (anything else `other`), each list
 * deduplicated and capped. Saleor's message is dropped on purpose: it can quote what
 * the buyer typed.
 */
export function saleorErrorSummary(errors: readonly { code?: string | null; field?: string | null }[]): {
	error_codes: string[];
	field_keys: string[];
} {
	const codes = new Set<string>();
	const fields = new Set<string>();
	for (const error of errors) {
		const code = saleorErrorCode(error.code);
		if (code && codes.size < MAX_REPORTED_SALEOR_ERRORS) codes.add(code);
		if (typeof error.field === "string" && fields.size < MAX_REPORTED_SALEOR_ERRORS)
			fields.add(
				(CHECKOUT_LOG_FIELD_KEYS as readonly string[]).includes(error.field) ? error.field : "other",
			);
	}
	return { error_codes: [...codes], field_keys: [...fields] };
}

/**
 * Why a payment submit did not go through. Machine-readable only — never widen this
 * with exception text, PSP messages or anything the visitor typed. `unknown` is the
 * fallback for a refusal the caller cannot attribute.
 */
export const PAYMENT_SUBMIT_BLOCK_REASONS = [
	"processing",
	"delivery_method_invalid",
	"loyalty_operation_active",
	"pricing_locked",
	"payment_method_unavailable",
	"loyalty_credit_pending",
	"loyalty_price_unconfirmed",
	"total_missing",
	"summary_not_confirmed",
	"vorkasse_attempt_open",
	"br_tax_id_required",
	"billing_update_failed",
	"billing_validation_failed",
	"no_gateway",
	"test_payments_only",
	"unknown",
] as const;
export type PaymentSubmitBlockReason = (typeof PAYMENT_SUBMIT_BLOCK_REASONS)[number];

/** How the refusal surfaced: tap on a disabled control, a pre-submit guard, form validation, a failed mutation. */
export const PAYMENT_SUBMIT_BLOCK_TRIGGERS = [
	"disabled_tap",
	"guard",
	"validation",
	"mutation_error",
] as const;
export type PaymentSubmitBlockTrigger = (typeof PAYMENT_SUBMIT_BLOCK_TRIGGERS)[number];

/** The concrete control that attempted the submit. */
export const PAYMENT_SUBMIT_SURFACES = [
	"sticky_bar",
	"inline_button",
	"form_submit",
	"retry_banner",
	"popup_restart",
	"popup_recovery",
	"resume",
	"enter_key",
] as const;
export type PaymentSubmitSurface = (typeof PAYMENT_SUBMIT_SURFACES)[number];

/** Why the cart's checkout CTA did not navigate. */
export const CART_CHECKOUT_BLOCK_REASONS = [
	"totals_updating",
	"checkout_unavailable",
	"minimum_order_not_met",
	"no_checkout",
	"mutation_in_flight",
	"unknown",
] as const;
export type CartCheckoutBlockReason = (typeof CART_CHECKOUT_BLOCK_REASONS)[number];

/**
 * Why a loyalty credit request was not sent at all (`loyalty_credit_result` with
 * `outcome: "blocked"`). Client-side refusals only; what the server answers is an
 * `error_code`, not a block reason. `unknown` is the fallback for any other value.
 */
export const LOYALTY_CREDIT_BLOCK_REASONS = [
	"invalid_points",
	"operation_busy",
	"pricing_not_ready",
	"legacy_voucher_active",
	"unknown",
] as const;
export type LoyaltyCreditBlockReason = (typeof LOYALTY_CREDIT_BLOCK_REASONS)[number];

/**
 * The type part of a Saleor global id (base64 of `Type:pk`, e.g. `ShippingMethod` for a
 * built-in method, `app` for one an app provides), never the id itself. `null` without
 * an id, `other` when the id does not decode to a type name.
 */
export function graphqlIdType(id: string | null | undefined): string | null {
	if (typeof id !== "string" || !id) return null;
	try {
		const type = atob(id).split(":")[0] ?? "";
		return /^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(type) ? type : "other";
	} catch {
		return "other";
	}
}

/**
 * A configured name as a groupable slug (`DHL Express` -> `dhl_express`), capped at 40
 * characters; `null` when nothing is left. Only for names the shop configures, such as
 * delivery methods, never for anything the buyer typed.
 */
export function nameSlug(name: string | null | undefined): string | null {
	if (typeof name !== "string") return null;
	const slug = name
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 40)
		.replace(/_+$/, "");
	return slug || null;
}

/**
 * Channel-currency amount rounded to cents; `null` for missing or non-finite input.
 * The `Number.EPSILON` nudge lifts binary half-cents (1.005 is stored as 1.00499…) to
 * the next cent, the same convention as `roundCommerceMoney` in commerce-data.ts and
 * `roundMoney` in cart/commerce-summary.ts. Neither is exported; this stays the
 * client-safe copy instead of importing their modules.
 */
export function roundMoney2(value: number | null | undefined): number | null {
	if (typeof value !== "number" || !Number.isFinite(value)) return null;
	return Math.round((value + Number.EPSILON) * 100) / 100;
}

export type CheckoutTotalBucket = "lt_100" | "100_250" | "250_400" | "gte_400" | "unknown";

/**
 * Coarse order-size band for segmenting blocks without sending exact carts. Lower
 * bounds are inclusive (100 → `100_250`). Input is the channel-currency total, see above.
 */
export function totalBucket(value: number | null | undefined): CheckoutTotalBucket {
	if (typeof value !== "number" || !Number.isFinite(value)) return "unknown";
	if (value < 100) return "lt_100";
	if (value < 250) return "100_250";
	if (value < 400) return "250_400";
	return "gte_400";
}
